// Full-screen consent form — read-and-accept flow with a drawn signature.
//
// Route: /consent/:chartId/:token   (top-level, no CRM sidebar, PUBLIC)
//   :chartId = chart row id · :token = the consent entry's unique_id, an
//   unguessable capability token carried in the URL.
//
// Auth model: ANONYMOUS / standalone — the token in the URL *is* the
// credential, so the page works on any device (e.g. the patient's own phone),
// no login required. It talks to the anon API:
//   read   GET  /api/consents/:token?chart_id=:chartId  → { consent, patient, treatment }
//   accept POST /api/consents/:token  { chart_id, signature } → { ok, accept_at }
// Those endpoints reach Xano via a server-side XANO_SERVICE_TOKEN (see
// functions/api/consents/[token].js). If that secret is unset they return 501
// and this page shows a "not configured" message.
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  ClipboardSignature,
  Loader2,
  AlertTriangle,
  Check,
  CheckCircle2,
  Clock,
  ShieldX,
  Eraser,
} from 'lucide-react'
import BrandMark from '../components/BrandMark.jsx'
import ConsentDocViewer from '../components/ConsentDocViewer.jsx'
import { CONSENT_DOCS, resolveConsentSlug } from '../data/consentDocs.js'
import { CONSENT_GENERIC, CONSENT_AGREE } from '../lib/documents/consent.js'

// Page strings. The ไทย/EN toggle switches the whole page AND the document,
// like patient_portal ConsentSign.jsx. The generic fallback + acknowledgement
// come from the PDF template so the screen and the filed PDF say the same thing.
const T = {
  th: {
    patient: 'ผู้ป่วย', treatment: 'การรักษา',
    docTitleFallback: 'หนังสือแสดงความยินยอม',
    docNote: 'หมายเหตุจากทันตแพทย์',
    agree: CONSENT_AGREE.th,
    signature: 'ลายเซ็นผู้ให้ความยินยอม',
    clear: 'ล้าง',
    confirm: 'ยอมรับและให้ความยินยอม',
    needSignature: 'กรุณาลงลายเซ็น',
    expiredErr: 'ใบยินยอมหมดอายุแล้ว',
    saveFailed: 'บันทึกไม่สำเร็จ',
    pdfFailed: 'สร้างเอกสาร PDF ไม่สำเร็จ ยังไม่ได้บันทึกการยินยอม กรุณาลองใหม่',
    acceptedTitle: 'ยอมรับเรียบร้อยแล้ว',
    acceptedBody: (when) => `ใบยินยอมนี้ได้รับการยอมรับเมื่อ ${when}`,
    expiredTitle: 'ใบยินยอมหมดอายุ',
    expiredBody: 'ใบยินยอมนี้หมดอายุแล้ว กรุณาให้เจ้าหน้าที่ต่ออายุในระบบก่อนให้ความยินยอม',
    genericIntro: CONSENT_GENERIC.th.intro,
    genericClauses: CONSENT_GENERIC.th.clauses,
  },
  en: {
    patient: 'Patient', treatment: 'Treatment',
    docTitleFallback: 'Informed Consent',
    docNote: 'Note from the dentist',
    agree: CONSENT_AGREE.en,
    signature: 'Signature',
    clear: 'Clear',
    confirm: 'Confirm & Sign',
    needSignature: 'Please sign first',
    expiredErr: 'This consent has expired',
    saveFailed: 'Could not save',
    pdfFailed: 'Could not create the PDF. Consent was NOT saved, please try again',
    acceptedTitle: 'Successfully signed',
    acceptedBody: (when) => `This consent was accepted on ${when}`,
    expiredTitle: 'Consent expired',
    expiredBody: 'This consent has expired. Please ask the staff to renew it before signing.',
    genericIntro: CONSENT_GENERIC.en.intro,
    genericClauses: CONSENT_GENERIC.en.clauses,
  },
}

export default function ConsentForm() {
  const { chartId, token } = useParams()
  // Patient id rides in ?p= — Xano has no GET /chart/{id}, so the API finds the
  // chart inside /chart?patient_id=… (same approach as the Patient Portal).
  const [search] = useSearchParams()
  const patientId = search.get('p') || ''

  const [data, setData] = useState(null) // { consent, patient, treatment }
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState(null)
  const [notFound, setNotFound] = useState(false)

  const [lang, setLang] = useState('th') // 'th' | 'en' — drives the whole page
  const t = T[lang]

  const [agreed, setAgreed] = useState(false)
  const [hasSignature, setHasSignature] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const [acceptErr, setAcceptErr] = useState(null)
  const [acceptedAt, setAcceptedAt] = useState(null)

  const padRef = useRef(null)

  async function load() {
    setLoading(true); setLoadErr(null); setNotFound(false)
    try {
      const r = await fetch(`/api/consents/${encodeURIComponent(token)}?chart_id=${encodeURIComponent(chartId)}&patient_id=${encodeURIComponent(patientId)}`)
      if (r.status === 404) { setNotFound(true); return }
      if (r.status === 501) { setLoadErr('ระบบใบยินยอมยังไม่ได้ตั้งค่า (XANO_SERVICE_TOKEN)'); return }
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      setData(await r.json())
    } catch (e) {
      setLoadErr(e?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() /* eslint-disable-next-line */ }, [chartId, token])

  const consent = data?.consent || null
  const patient = data?.patient || null
  const treatment = data?.treatment || null

  // Structured document for the type (9 of 11 types); null → generic fallback.
  const slug = resolveConsentSlug(consent?.type)
  const doc = slug ? CONSENT_DOCS[slug]?.[lang] : null

  const now = Date.now()
  const alreadyAccepted = !!(consent?.accept || consent?.accept_at || acceptedAt)
  const expired = !alreadyAccepted && consent?.expired_date && now > consent.expired_date

  const label =
    consent?.type === 'กำหนดเอง' && consent?.custom_label
      ? consent.custom_label
      : consent?.type || '—'

  async function handleAccept() {
    if (!agreed || !hasSignature || accepting) return
    const signature = padRef.current?.toDataURL() || null
    if (!signature) { setAcceptErr(t.needSignature); return }
    setAccepting(true); setAcceptErr(null)
    try {
      const r = await fetch(`/api/consents/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chart_id: chartId, patient_id: patientId, signature, lang }),
      })
      if (r.status === 409) { setAcceptedAt(Date.now()); return } // already accepted
      if (r.status === 410) { setAcceptErr(t.expiredErr); await load(); return }
      if (r.status === 501 || r.status === 502) {
        const j = await r.json().catch(() => ({}))
        if (j.error === 'pdf_failed') throw new Error(`${t.pdfFailed} (${j.reason || r.status})`)
      }
      if (!r.ok) {
        const txt = await r.text().catch(() => '')
        throw new Error(`HTTP ${r.status}: ${txt || r.statusText}`)
      }
      const j = await r.json().catch(() => ({}))
      setAcceptedAt(j.accept_at || Date.now())
      setData((d) => (d ? { ...d, consent: { ...d.consent, accept: true, accept_at: j.accept_at || Date.now(), signature } } : d))
    } catch (e) {
      setAcceptErr(e?.message || t.saveFailed)
    } finally {
      setAccepting(false)
    }
  }

  /* ---------------------------------------------------------------- states */

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 py-24 text-slate-400">
          <Loader2 size={18} className="animate-spin" /> กำลังโหลด…
        </div>
      </Shell>
    )
  }
  if (notFound || (!consent && !loadErr)) {
    return (
      <Shell>
        <Notice tone="rose" icon={ShieldX} title="ไม่พบใบยินยอม">
          ลิงก์นี้ไม่ถูกต้องหรือใบยินยอมถูกลบไปแล้ว กรุณาให้เจ้าหน้าที่สร้างใบยินยอมใหม่
        </Notice>
      </Shell>
    )
  }
  if (loadErr) {
    return (
      <Shell>
        <Notice tone="rose" icon={AlertTriangle} title="โหลดข้อมูลไม่สำเร็จ">{loadErr}</Notice>
      </Shell>
    )
  }

  return (
    <Shell right={<LangToggle lang={lang} setLang={setLang} />}>
      {/* Context header */}
      <div className="mb-5 rounded-2xl bg-white ring-1 ring-slate-200 overflow-hidden">
        <div className="px-5 py-4 flex items-start gap-3 border-b border-slate-100">
          <span className="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center ring-1 ring-brand-200 flex-none">
            <ClipboardSignature size={18} strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <span className="inline-block px-2 py-0.5 rounded-full text-[11.5px] font-semibold bg-brand-50 text-brand-700 ring-1 ring-brand-200">
              {label}
            </span>
            <h1 className="mt-1.5 text-[17px] font-semibold text-slate-900 leading-snug">{t.docTitleFallback}</h1>
          </div>
        </div>
        <dl className="px-5 py-3 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 text-[13px]">
          <Field label={t.patient} value={patient?.name} />
          <Field label="HN" value={patient?.hn} mono />
          <Field label={t.treatment} value={treatment?.name} />
        </dl>
      </div>

      {/* Dentist's note */}
      {consent.note && consent.note.trim() && (
        <section className="mb-5 rounded-2xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3">
          <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-amber-700 mb-0.5">{t.docNote}</div>
          <p className="text-[13.5px] text-amber-900 whitespace-pre-line">{consent.note}</p>
        </section>
      )}

      {/* Document — structured per type, or the generic fallback */}
      {doc ? (
        <ConsentDocViewer doc={doc} />
      ) : (
        <article className="rounded-2xl bg-white ring-1 ring-slate-200 px-5 sm:px-7 py-6 text-[14px] leading-relaxed text-slate-700 space-y-3">
          <p>{t.genericIntro(patient?.name || '—', patient?.hn)}</p>
          <ol className="list-decimal pl-5 space-y-2 marker:text-brand-500">
            {t.genericClauses.map((c, i) => <li key={i}>{c}</li>)}
          </ol>
        </article>
      )}

      {/* Accept zone */}
      <div className="mt-5">
        {alreadyAccepted ? (
          <Notice tone="emerald" icon={CheckCircle2} title={t.acceptedTitle}>
            {t.acceptedBody(formatDT(consent.accept_at || acceptedAt))}
            {consent.signer_name ? ` — ${consent.signer_name}` : ''}
            {consent.signature && (
              <img src={consent.signature} alt="signature" className="mt-3 h-20 bg-white rounded-lg ring-1 ring-emerald-200 object-contain" />
            )}
          </Notice>
        ) : expired ? (
          <Notice tone="rose" icon={Clock} title={t.expiredTitle}>{t.expiredBody}</Notice>
        ) : (
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-5 space-y-4">
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-[13.5px] text-slate-700 leading-relaxed">{t.agree}</span>
            </label>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500">
                  {t.signature}
                </label>
                <button
                  type="button"
                  onClick={() => { padRef.current?.clear(); setHasSignature(false) }}
                  className="inline-flex items-center gap-1 text-[11.5px] font-medium text-slate-500 hover:text-slate-800"
                >
                  <Eraser size={12} strokeWidth={2.25} /> {t.clear}
                </button>
              </div>
              <SignaturePad ref={padRef} onChange={setHasSignature} />
            </div>

            {acceptErr && (
              <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-rose-700 text-[13px] flex items-start gap-2">
                <AlertTriangle size={14} className="flex-none mt-0.5" />
                <div>{acceptErr}</div>
              </div>
            )}

            <button
              type="button"
              onClick={handleAccept}
              disabled={!agreed || !hasSignature || accepting}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-[15px] font-semibold bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {accepting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} strokeWidth={2.5} />}
              {t.confirm}
            </button>
          </div>
        )}
      </div>
    </Shell>
  )
}

function LangToggle({ lang, setLang }) {
  return (
    <div className="inline-flex p-0.5 rounded-full bg-slate-100 ring-1 ring-slate-200">
      {['th', 'en'].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLang(l)}
          className={`px-3 py-1 rounded-full text-[12.5px] font-semibold transition-colors ${
            lang === l ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          {l === 'th' ? 'ไทย' : 'EN'}
        </button>
      ))}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Signature pad — pointer-based canvas (works with touch + mouse)            */
/* -------------------------------------------------------------------------- */

const SignaturePad = forwardRef(function SignaturePad({ onChange }, ref) {
  const canvasRef = useRef(null)
  const drawing = useRef(false)
  const dirty = useRef(false)

  useImperativeHandle(ref, () => ({
    clear() {
      const c = canvasRef.current
      if (!c) return
      c.getContext('2d').clearRect(0, 0, c.width, c.height)
      dirty.current = false
    },
    toDataURL() {
      return dirty.current ? canvasRef.current?.toDataURL('image/png') : null
    },
  }))

  // Size the backing store to its CSS box × devicePixelRatio for crisp lines.
  useEffect(() => {
    const c = canvasRef.current
    if (!c) return
    const ratio = window.devicePixelRatio || 1
    const rect = c.getBoundingClientRect()
    c.width = Math.round(rect.width * ratio)
    c.height = Math.round(rect.height * ratio)
    const ctx = c.getContext('2d')
    ctx.scale(ratio, ratio)
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#0f172a'
  }, [])

  function pos(e) {
    const rect = canvasRef.current.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }
  function start(e) {
    e.preventDefault()
    drawing.current = true
    const ctx = canvasRef.current.getContext('2d')
    const { x, y } = pos(e)
    ctx.beginPath(); ctx.moveTo(x, y)
    canvasRef.current.setPointerCapture?.(e.pointerId)
  }
  function move(e) {
    if (!drawing.current) return
    e.preventDefault()
    const ctx = canvasRef.current.getContext('2d')
    const { x, y } = pos(e)
    ctx.lineTo(x, y); ctx.stroke()
    if (!dirty.current) { dirty.current = true; onChange?.(true) }
  }
  function end() { drawing.current = false }

  return (
    <canvas
      ref={canvasRef}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerLeave={end}
      className="w-full h-40 rounded-xl bg-slate-50 ring-1 ring-slate-200 touch-none cursor-crosshair"
    />
  )
})

/* -------------------------------------------------------------------------- */

function Shell({ children, right }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <BrandMark size={26} />
          {right}
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 animate-ss-pop-in">{children}</main>
    </div>
  )
}

function Field({ label, value, mono }) {
  return (
    <div>
      <dt className="text-[10.5px] uppercase tracking-[0.12em] font-semibold text-slate-400">{label}</dt>
      <dd className={`text-slate-800 ${mono ? 'font-mono text-[12.5px]' : ''}`}>{value || '—'}</dd>
    </div>
  )
}

const TONES = {
  rose:    { bg: 'bg-rose-50',    ring: 'ring-rose-200',    text: 'text-rose-700',    icon: 'text-rose-500' },
  emerald: { bg: 'bg-emerald-50', ring: 'ring-emerald-200', text: 'text-emerald-800', icon: 'text-emerald-600' },
}
function Notice({ tone = 'rose', icon: Icon, title, children }) {
  const t = TONES[tone] || TONES.rose
  return (
    <div className={`rounded-2xl ${t.bg} ring-1 ${t.ring} px-5 py-5 flex items-start gap-3`}>
      {Icon && <Icon size={20} className={`flex-none mt-0.5 ${t.icon}`} />}
      <div>
        <div className={`text-[14.5px] font-semibold ${t.text}`}>{title}</div>
        <div className={`text-[13.5px] mt-0.5 ${t.text} opacity-90`}>{children}</div>
      </div>
    </div>
  )
}

function formatDT(v) {
  if (v == null || v === '') return ''
  const d = new Date(typeof v === 'number' ? v : v)
  if (isNaN(d.getTime())) return String(v)
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}
